import React from 'react'
import styled, { css } from 'styled-components'
import { Controller } from 'react-hook-form'
import {
  Block,
  Button,
  Input,
  Select,
  TextArea,
  P as TypographyP,
  H3 as TypographyH3
} from '@mote/ui'

// TODO: Move this to @mote/ui in future
export const FormInput = ({
  name,
  className,
  type = 'input',
  label,
  options,
  required = true,
  disabled = false,
  invalid = false,
  value: _value,
  currentValue,
  error,
  styles = { wrapper: {}, input: {} },
  style,
  control,
  shouldUnregister = false,
  onChange,
  parseValue = true,
  ...rest
}) => {
  const { Input, getInputProps } = React.useMemo(() => {
    if (type === 'select')
      return {
        Input: StyledSelect,
        getInputProps: value => ({ currentValue: value.toString(), options })
      }

    return {
      Input: type === 'textarea' ? StyledTextArea : StyledInput,
      getInputProps: value => ({ value: value.toString() })
    }
  }, [type, options])

  function parseInputValue(value) {
    let newValue = ''

    if (value !== '') {
      try {
        newValue = Number(value)
      } catch (e) {}

      if (Number.isNaN(newValue)) {
        newValue = value
      }
    }

    return newValue
  }

  return (
    <FormGroup className={className} style={styles.wrapper}>
      {label && <Text required={required}>{label}</Text>}
      <Controller
        control={control}
        name={name}
        rules={{ required, disabled }}
        shouldUnregister={shouldUnregister}
        render={({
          field: { onChange: onFormValueChange, value, name, ref },
          fieldState: { invalid, error }
        }) => {
          return (
            <>
              <Input
                ref={ref}
                name={name}
                invalid={invalid}
                disabled={disabled}
                style={styles.input}
                onChange={event => {
                  const { value } = event.target
                  const parsedValue = parseValue
                    ? parseInputValue(value)
                    : value

                  const customEvent = {
                    target: {
                      value: parsedValue
                    },
                    nativeEvent: event
                  }

                  onChange?.(customEvent)
                  onFormValueChange(parsedValue)
                }}
                {...getInputProps(value)}
                {...rest}
              />
              {invalid && error?.message && (
                <FormExplanation isError>{error.message}</FormExplanation>
              )}
            </>
          )
        }}
      />
    </FormGroup>
  )
}

export const Root = styled.div`
  margin-top: 50px;
  flex-direction: column;
  padding-bottom: 50px;
`

export const VanillaRoot = styled(Root)`
  text-align: start;
  justify-content: start;
`

export const CenteredRoot = styled(Root)`
  text-align: center;
  justify-content: center;
  background: #f9f9fb;
`

export const Fonts = styled.div`
  font-family: DM Sans;
  line-height: 200%;
`

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 60px;
  width: 100%;
`

export const CapsFormLabel = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  margin: 11px 0;
`

export const FormExplanation = styled.span`
  color: #232323;
  font-weight: normal;
  font-size: 16px;
  line-height: 140%;

  ${({ isError }) =>
    isError &&
    css`
      color: #f44336;
    `}
`

export const H1 = styled(Fonts)`
  margin: 0 auto;
  margin-top: 50px;
  font-weight: bold;
  font-size: 32px;
  line-height: 120%;
`

export const H2 = styled(Fonts)`
  margin-top: 40px;
  font-size: 28px;
  line-height: 36px;
`

export const H3 = styled(Fonts)`
  margin-top: 30px;
  font-size: 21px;
  line-height: 27px;
`

export const H3NoTop = styled(H3)`
  margin-top: 0px;
`

export const P = styled(Fonts)`
  font-style: normal;
  font-weight: normal;
  align-items: start;
  margin-top: 20px;
  margin-bottom: 20px;
  font-size: 16px;
`

export const CenteredP = styled(P)`
  align-items: center;
`

export const LeftP = styled(P)``

export const LeftBoldP = styled(LeftP)`
  font-weight: bold;
`

export const Promoted = styled(Fonts)`
  color: #ac0ae8;
`

export const StyledSelect = styled(Select)`
  font-size: 16px;
  height: 65px;
  padding: 0 40px 0 24px;
`

export const StyledInput = styled(Input)`
  font-size: 16px;
  height: 65px;
  padding: 0 24px;
`

export const Title = styled(TypographyH3)`
  color: #232323;
  font-size: 21px !important;
  font-weight: 500 !important;
  line-height: 140% !important;
`

export const Subtitle = styled(TypographyP)`
  color: #646f7b;
  font-size: 16px !important;
  font-weight: 400 !important;
  line-height: 140% !important;
`

export const Text = styled(TypographyP)`
  color: #232323;
  text-transform: uppercase;
  font-size: 15px !important;
  line-height: 120% !important;
  font-weight: 400 !important;

  ${({ required = true }) =>
    required &&
    css`
      &::after {
        content: '*';
        color: #f44336;
        margin-left: 4px;
      }
    `}
`

export const Link = styled(Button)``

export const FormGroup = styled(Block)`
  gap: 12px;
`

export const StyledTextArea = styled(TextArea)`
  background: #ffffff;
  padding: 16px 24px;
`
